import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// トランジション状態の定義
export type TransitionState = 'idle' | 'expanding' | 'transitioning' | 'revealing';

// アニメーションタイミング設定
export const ANIMATION_TIMING = {
  EXPAND_DURATION: 400,    // 拡大アニメーション時間
  COLOR_CHANGE_DELAY: 0,   // 即座にテキスト色変更
  FADE_DELAY: 100,         // ページ遷移後の待機時間
  FADE_DURATION: 400       // フェードイン時間
} as const;

// コンテキストの型定義
interface TransitionContextType {
  // 状態
  transitionState: TransitionState;
  isTransitioning: boolean;
  targetRoute: string | null;
  shouldUseWhiteText: boolean; // ヘッダー・フッターの色制御用
  shouldFadeOutText: boolean;  // ホームページテキストフェードアウト制御用
  shouldShowProjectText: boolean; // プロジェクトページテキスト表示制御用
  
  // アクション
  startTransition: (route: string) => Promise<void>;
  setTransitionState: (state: TransitionState) => void;
  resetTransition: () => void;
}

// デフォルト値
const defaultContextValue: TransitionContextType = {
  transitionState: 'idle',
  isTransitioning: false,
  targetRoute: null,
  shouldUseWhiteText: false,
  shouldFadeOutText: false, // ホームページテキスト用
  shouldShowProjectText: false, // プロジェクトページテキスト用
  startTransition: async () => {},
  setTransitionState: () => {},
  resetTransition: () => {}
};

// コンテキスト作成
const TransitionContext = createContext<TransitionContextType>(defaultContextValue);

// カスタムフック
export const useTransition = () => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error('useTransition must be used within a TransitionProvider');
  }
  return context;
};

// プロバイダーコンポーネントの Props
interface TransitionProviderProps {
  children: ReactNode;
  navigate: (route: string) => void; // React Router の navigate 関数
}

// プロバイダーコンポーネント
export const TransitionProvider: React.FC<TransitionProviderProps> = ({ 
  children, 
  navigate 
}) => {
  const [transitionState, setTransitionState] = useState<TransitionState>('idle');
  const [targetRoute, setTargetRoute] = useState<string | null>(null);
  const [shouldUseWhiteText, setShouldUseWhiteText] = useState(false);
  const [shouldFadeOutText, setShouldFadeOutText] = useState(false); // ホームページテキスト用
  const [shouldShowProjectText, setShouldShowProjectText] = useState(false); // プロジェクトページテキスト用

  // トランジション実行中かどうか
  const isTransitioning = transitionState !== 'idle';

  // プロジェクトページテキスト表示制御
  useEffect(() => {
    if (transitionState === 'revealing') {
      // 少し遅延してからプロジェクトテキストを表示
      const showTimer = setTimeout(() => {
        setShouldShowProjectText(true);
      }, 200);
      return () => clearTimeout(showTimer);
    } else if (transitionState === 'expanding') {
      // トランジション開始時に非表示
      setShouldShowProjectText(false);
    }
  }, [transitionState]);

  // フェードアウト制御（ホームページのみ）
  useEffect(() => {
    console.log('🎭 Fade out effect - transitionState:', transitionState, 'shouldFadeOutText:', shouldFadeOutText);
    
    if (transitionState === 'expanding') {
      // expanding状態になった瞬間にフェードアウト開始
      console.log('🎭 STARTING TEXT FADE OUT');
      setShouldFadeOutText(true);
    } else if (transitionState === 'idle') {
      // アイドル状態ではフェードインに戻す（少し遅延）
      const resetTimer = setTimeout(() => {
        console.log('🎭 RESTORING TEXT VISIBILITY');
        setShouldFadeOutText(false);
      }, 200); // 200ms遅延


      return () => clearTimeout(resetTimer);
    }
  }, [transitionState, shouldFadeOutText]);

  // テキスト色の制御（既存）
  useEffect(() => {
    console.log('🎨 Text color effect - transitionState:', transitionState, 'shouldUseWhiteText:', shouldUseWhiteText);

    if (transitionState === 'expanding') {
      // expanding状態になった瞬間に白文字に変更
      console.log('🎨 IMMEDIATELY changing header/footer text to white');
      setShouldUseWhiteText(true);
    } else if (transitionState === 'idle') {
      // アイドル状態では黒文字に戻す（ただし、遅延させる）
      const resetTimer = setTimeout(() => {
        console.log('🎨 Changing header/footer text back to black');
        setShouldUseWhiteText(false);
      }, 200); // 200ms遅延

      return () => clearTimeout(resetTimer);
    }
  }, [transitionState, shouldUseWhiteText]);

  // トランジション開始
  const startTransition = async (route: string): Promise<void> => {
    if (isTransitioning) {
      console.warn('Transition already in progress');
      return;
    }

    try {
      setTargetRoute(route);
      console.log(`Starting transition to: ${route}`);

      // Phase 1: 拡大アニメーション開始（同時にテキスト色変更＆フェードアウト）
      setTransitionState('expanding');
      
      // 拡大アニメーション完了まで待機
      await new Promise(resolve => 
        setTimeout(resolve, ANIMATION_TIMING.EXPAND_DURATION)
      );

      // Phase 2: ページ遷移
      setTransitionState('transitioning');
      navigate(route);

      // Phase 3: フェードイン準備（少し待機）
      await new Promise(resolve => 
        setTimeout(resolve, ANIMATION_TIMING.FADE_DELAY)
      );

      setTransitionState('revealing');

      // フェードイン完了まで待機
      await new Promise(resolve => 
        setTimeout(resolve, ANIMATION_TIMING.FADE_DURATION)
      );

      // トランジション完了
      resetTransition();

    } catch (error) {
      console.error('Transition failed:', error);
      resetTransition();
    }
  };

  // トランジションリセット
  const resetTransition = () => {
    setTransitionState('idle');
    setTargetRoute(null);
    console.log('Transition completed');
  };

  const value: TransitionContextType = {
    transitionState,
    isTransitioning,
    targetRoute,
    shouldUseWhiteText,
    shouldFadeOutText, // ホームページテキスト用
    shouldShowProjectText, // プロジェクトページテキスト用
    startTransition,
    setTransitionState,
    resetTransition
  };

  return (
    <TransitionContext.Provider value={value}>
      {children}
    </TransitionContext.Provider>
  );
};